// UpcomingMeetingsList.tsx

import { format, isAfter, parseISO, startOfDay } from "date-fns";
import { he } from "date-fns/locale";
import { CalendarClock, MapPin, User } from "lucide-react";
import { useGetMeetingsByMonthQuery } from "../../../api/calendarApi";

interface Props {
  mentorId: string;
}

const UpcomingMeetingsList = ({ mentorId }: Props) => {
  const { data: allMeetings = [], isLoading, error } = useGetMeetingsByMonthQuery({
    mentorId,
    month: format(new Date(), "yyyy-MM"),
  });

  const today = startOfDay(new Date());

  const upcomingMeetings = allMeetings.filter((meeting) => {
    if (!meeting.date) return false;
    const meetingDate = startOfDay(parseISO(meeting.date));
    return isAfter(meetingDate, today) || meetingDate.getTime() === today.getTime();
  });

  return (
    <div className="w-full max-w-md bg-secondary rounded-2xl shadow-2xl overflow-hidden border border-border flex flex-col">
      <div className="bg-gradient-to-r from-primary to-highlight p-6 text-white">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <CalendarClock className="w-6 h-6" />
            פגישות קרובות
          </h3>
        </div>
      </div>

      <div className="p-4 flex-grow overflow-y-auto">
        {isLoading ? (
          <div className="text-gray-400 text-center">טוען פגישות...</div>
        ) : error ? (
          <div className="text-red-500 text-center">שגיאה בטעינת פגישות</div>
        ) : upcomingMeetings.length === 0 ? (
          <div className="text-gray-500 text-center">אין פגישות להציג</div>
        ) : (
          <div className="space-y-3">
            {upcomingMeetings.map((meeting) => (
              <div
                key={meeting.id}
                className="bg-background border border-border rounded-lg p-3 hover:shadow-card transition-all flex flex-col gap-1"
              >
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-text-main text-base">
                    פגישה #{meeting.meeting_number || meeting.id.slice(0, 6)}
                  </h4>
                  <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-medium">
                    {meeting.date
                      ? format(new Date(meeting.date), "HH:mm", { locale: he })
                      : "אין שעה"}
                  </span>
                </div>

                {meeting.location && (
                  <div className="flex items-center gap-1 text-gray-600 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>מיקום: {meeting.location}</span>
                  </div>
                )}

                {meeting.attendees && meeting.attendees.length > 0 && (
                  <div className="flex items-center gap-1 text-gray-600 text-sm">
                    <User className="w-4 h-4" />
                    <span>משתתפים: {meeting.attendees.join(", ")}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingMeetingsList;
